/**
 * FDR Charting — the Charting tab of FDR → COA (operator order 2026-08-29):
 * the same board the internal COA uses, on FDR data only.
 *
 *   COA measures   the monitoring feed with scope=fdr: the server returns
 *                  only work on registered FDRs, never the organization's
 *                  own oversight (and the internal board never sees FDR).
 *   Scorecards     the vendors' scorecards: vendor → domain (sheet) → KPI,
 *                  monthly values against the benchmark, met or missed.
 *
 * Same functions on both: month, quarter, year with change against the prior
 * period, charts or the calendar table, CSV for Excel, and the detailed PDF
 * (logo, Deck Studio colors, every level under what is on screen).
 */
import React, { useEffect, useMemo, useState } from 'react';
import ChartingBoard from '../coa/ChartingBoard';
import { buildScorecardCharting } from './scorecardCharting';

const API = process.env.REACT_APP_API_URL || '';

export default function FdrCharting({ orgName = '' }) {
  const [source, setSource] = useState('coa'); // coa | scorecards
  const [feed, setFeed] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (source !== 'scorecards' || feed) return;
    fetch(`${API}/api/fdr/scorecards/charting?years=4`, { credentials: 'include' })
      .then((r) => r.json())
      .then((d) => { if (d.success) setFeed(d); else setErr(d.message || 'Could not load the scorecards.'); })
      .catch((e) => setErr(e.message));
  }, [source, feed]);

  const scorecard = useMemo(() => (feed ? buildScorecardCharting(feed) : null), [feed]);

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      <div className="px-8 pt-3 flex items-center gap-2 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Charting</span>
        <div role="tablist" aria-label="What to chart" className="inline-flex rounded-xl bg-slate-100 p-1 gap-0.5">
          {[['coa', 'COA measures', 'The FDR work plan: measures on registered FDRs, month over month against their benchmarks'],
            ['scorecards', 'Scorecards', 'The vendors’ scorecards: every KPI against its benchmark, by vendor and domain']].map(([v, l, t]) => (
            <button key={v} type="button" role="tab" aria-selected={source === v} title={t} onClick={() => setSource(v)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition ${source === v ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
              {l}
            </button>
          ))}
        </div>
        <span className="text-[11px] text-slate-400">FDR data only; the organization’s own oversight charts live in Precision COA.</span>
      </div>
      {source === 'coa' ? (
        <ChartingBoard scope="fdr" orgName={orgName} title="FDR COA charting" docTitle="FDR COA Charting"
          emptyHint="Sync FDR activities on the work plan and their results appear here as trends." />
      ) : err ? (
        <div className="py-24 text-center text-rose-600 text-sm">{err}</div>
      ) : !scorecard ? (
        <div className="py-24 text-center text-slate-400">Loading…</div>
      ) : (
        <ChartingBoard scope="fdr" orgName={orgName} measuresOverride={scorecard.measures} areasOverride={scorecard.areas}
          title="FDR scorecard charting" docTitle="FDR Scorecard Charting"
          emptyHint="Upload a vendor scorecard under Scorecards and its KPIs appear here as trends." />
      )}
    </div>
  );
}
